import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { GetUserConfigResponse, appApi } from 'api/api';
import { PersistConfig, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

export type Theme = 'light' | 'dark' | 'custom';

export interface BackgroundPattern {
  fileName: string;
  size: string;
}

export interface UserConfig {
  isOnboardingWidgetFullView: boolean;
  isSkippedInviteManagersBanner: boolean;
  isAutoRegistrationEnabled: boolean;
  theme: Theme;
  backgroundPattern?: BackgroundPattern;
  onboardingFinishedDate?: string;
  onboardingStartDate?: string;
  onboardingDurationMinutes?: number;
}

export const initialState: UserConfig = {
  isOnboardingWidgetFullView: true,
  isSkippedInviteManagersBanner: false,
  isAutoRegistrationEnabled: true,
  theme: 'light',
};

const persistConfig: PersistConfig<UserConfig> = {
  key: 'userConfig',
  storage,
  version: CACHE_STORE_VERSION,
};

function syncUserConfigReducer(
  state: UserConfig,
  action: PayloadAction<GetUserConfigResponse>,
): UserConfig {
  const frontendConfig = action.payload.detail.frontend_config;

  if (
    !frontendConfig ||
    !('state' in frontendConfig) ||
    typeof frontendConfig.state !== 'object' ||
    frontendConfig.state === null
  ) {
    return state;
  }

  try {
    const nextState = {
      ...frontendConfig.state,
    };

    delete nextState.startOnboardingDate;
    delete nextState.isInvitedAllEmployeesBannerVisible;
    delete nextState.isOnboardingWidgetFullView;
    delete nextState.onboardingDurationDays;

    if (nextState.isOnboardingV2Finished) {
      const date = new Date();

      date.setFullYear(2000);

      nextState.onboardingFinishedDate = date.toISOString();
      delete nextState.isOnboardingV2Finished;
    }

    return nextState;
  } catch (error) {
    console.error(error);

    return state;
  }
}

export const userConfigSlice = createSlice({
  name: 'userConfig',
  initialState,
  reducers: {
    toggleOnboardingWidgetFullView(state) {
      state.isOnboardingWidgetFullView = !state.isOnboardingWidgetFullView;
    },

    setTheme(state, action: PayloadAction<Theme>) {
      state.theme = action.payload;
    },
    skipInviteManagersBanner(state) {
      state.isSkippedInviteManagersBanner = true;
    },
    disableAutoActivation(state) {
      state.isAutoRegistrationEnabled = false;
    },
    toggleBackground: (state) => {
      if (state.theme === 'custom') {
        state.theme = 'light';
      } else {
        state.theme = 'custom';
      }
    },
    setBackgroundPattern(state, action: PayloadAction<BackgroundPattern>) {
      state.backgroundPattern = action.payload;
    },
    clearBackground(state) {
      delete state.backgroundPattern;
    },
    finishOnboarding(state) {
      state.onboardingFinishedDate = new Date().toISOString();

      if (state.onboardingStartDate) {
        const start = new Date(state.onboardingStartDate);
        const end = new Date(state.onboardingFinishedDate);

        const diff = end.getTime() - start.getTime();
        state.onboardingDurationMinutes = Math.round(diff / 60000);
      }
    },
    startOnboarding(state) {
      state.onboardingStartDate = new Date().toISOString();
    },
  },
  selectors: {
    selectIsOnboardingWidgetFullView: (state) =>
      state.isOnboardingWidgetFullView,
    selectUserConfig: (state) => state,
    selectIsSkippedInviteManagersBanner: (state) =>
      state.isSkippedInviteManagersBanner,
    selectIsAutoRegistrationEnabled: (state) => state.isAutoRegistrationEnabled,
    selectTheme: (state) => state.theme,
    selectBackgroundPattern: (state) => state.backgroundPattern,
    selectIsOnboardingV2Finished: (state) => !!state.onboardingFinishedDate,
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      appApi.endpoints.getUserConfig.matchFulfilled,
      syncUserConfigReducer,
    );
  },
});

export const userConfigPersistedReducer = persistReducer(
  persistConfig,
  userConfigSlice.reducer,
);
